import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart2, BookOpen, Users, CreditCard, Target } from 'lucide-react';
import { supabase } from '../lib/supabase';
import BotPanel from '../components/BotPanel';
import ReferralProgress from '../components/ReferralProgress';
import { useBotAPI, type SetupTrackingResponse } from '../lib/useBotAPI';

interface DashboardProps {
  session: any;
  isAdmin?: boolean;
}

interface DashboardStats {
  analysesCount: number;
  tradesCount: number;
  referralsCount: number;
  subscriptionPlan: string;
}

interface UserProfile {
  full_name: string | null;
  created_at: string;
}

const Dashboard: React.FC<DashboardProps> = ({ session, isAdmin = false }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    analysesCount: 0,
    tradesCount: 0,
    referralsCount: 0,
    subscriptionPlan: 'Gratuito'
  });
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { data: tracking } = useBotAPI<SetupTrackingResponse>('/api/setups-tracking');

  useEffect(() => {
    if (!session?.user) {
      navigate('/login');
      return;
    }
    fetchDashboardStats();
    fetchUserProfile();
  }, [session, navigate]);

  const fetchUserProfile = async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, created_at')
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          const { data: userData } = await supabase.auth.getUser();
          const { error: insertError } = await supabase
            .from('profiles')
            .insert([{
              id: session.user.id,
              full_name: userData.user?.user_metadata?.full_name || null
            }])
            .select('full_name, created_at')
            .single();

          if (!insertError) {
            setProfile({
              full_name: userData.user?.user_metadata?.full_name || null,
              created_at: new Date().toISOString()
            });
          }
        }
      } else {
        setProfile(profileData);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      if (!session?.user?.id) return;
      
      const userId = session.user.id;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const [analysesResult, tradesResult, referralsResult, subscriptionResult] = await Promise.all([
        supabase
          .from('analyses')
          .select('*', { count: 'exact' })
          .eq('user_id', userId)
          .gte('created_at', thirtyDaysAgo.toISOString()),
        supabase
          .from('trading_journal')
          .select('*', { count: 'exact' })
          .eq('user_id', userId),
        supabase
          .from('referral_sales')
          .select('*', { count: 'exact' })
          .eq('referrer_id', userId)
          .neq('referral_id', userId)
          .eq('status', 'active'),
        supabase
          .from('subscriptions')
          .select('status')
          .eq('user_id', userId)
          .maybeSingle()
      ]);

      setStats({
        analysesCount: analysesResult.count || 0,
        tradesCount: tradesResult.count || 0,
        referralsCount: referralsResult.count || 0,
        subscriptionPlan: subscriptionResult.data?.status === 'active' ? 'Premium' : 'Gratuito'
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold text-white">
            Bienvenido{profile?.full_name ? `, ${profile.full_name}` : ''}
          </h1>
          <p className="mt-2 text-gray-400">
            Aquí tienes un resumen de tu actividad reciente
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Mentor Live Panel */}
        <BotPanel isAdmin={isAdmin} />

        {/* Referral Progress - only shows for subscribers */}
        <ReferralProgress
          referralsCount={stats.referralsCount}
          hasSubscription={stats.subscriptionPlan === 'Premium'}
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <Target className="h-8 w-8 text-blue-500" />
              <span className="text-3xl font-bold text-white">
                {tracking?.setups?.filter(s => s.setup_type === 'live').length || 0}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Setups Detectados</h3>
            <p className="mt-1 text-sm text-gray-400">hoy por el mentor</p>
          </div>

          <Link
            to="/dashboard/journal"
            className="card hover:border-white/20 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <BookOpen className="h-8 w-8 text-green-500" />
              <span className="text-3xl font-bold text-white">
                {stats.tradesCount}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Operaciones</h3>
            <p className="mt-1 text-sm text-gray-400">en el diario de trading</p>
          </Link>

          <Link
            to="/dashboard/affiliates"
            className="card hover:border-white/20 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-purple-500" />
              <span className="text-3xl font-bold text-white">
                {stats.referralsCount}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Referidos Activos</h3>
            <p className="mt-1 text-sm text-gray-400">en el programa de embajadores</p>
          </Link>

          <Link
            to="/dashboard/subscription"
            className="card hover:border-white/20 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <CreditCard className="h-8 w-8 text-yellow-500" />
              <span className="text-xl font-bold text-white">
                {stats.subscriptionPlan}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Plan Actual</h3>
            <p className="mt-1 text-sm text-gray-400">actualiza para más beneficios</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;